import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Receipt,
  Wallet,
  BarChart3,
  PieChart as PieChartIcon,
  X,
  Save,
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';
import {
  getAllExpenses,
  addExpense,
  updateExpense,
  deleteExpense,
  getAllAdvances,
  addAdvance,
  updateAdvance,
  updateAdvanceStatus,
  deleteAdvance,
  getAllExpenseCategories,
} from '../../services/expenseService';
import { getAllStaff } from '../../services/staffService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const ExpenseManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expenses'); // expenses, advances, reports
  
  // Data states
  const [expenses, setExpenses] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Date range for reports
  const today = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(today);
  
  // Modal states
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // add or edit
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    title: '',
    category: '',
    amount: '',
    expenseDate: today,
    description: '',
    paymentMethod: 'cash',
    status: 'pending',
    receiptNumber: '',
    vendor: '',
  });
  
  const [advanceForm, setAdvanceForm] = useState({
    staffId: '',
    staffName: '',
    amount: '',
    requestDate: today,
    reason: '',
    status: 'pending',
    repaymentMethod: 'salary_deduction',
    installments: '1',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [expensesData, advancesData, categoriesData, staffData] = await Promise.all([
        getAllExpenses(),
        getAllAdvances(),
        getAllExpenseCategories(),
        getAllStaff(),
      ]);
      
      setExpenses(expensesData);
      setAdvances(advancesData);
      setCategories(categoriesData.length > 0 ? categoriesData : getDefaultCategories());
      setStaff(staffData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load expense data');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultCategories = () => [
    { id: '1', name: 'Office Supplies', color: '#3B82F6' },
    { id: '2', name: 'Utilities', color: '#10B981' },
    { id: '3', name: 'Rent', color: '#F59E0B' },
    { id: '4', name: 'Salaries', color: '#EF4444' },
    { id: '5', name: 'Marketing', color: '#8B5CF6' },
    { id: '6', name: 'Travel', color: '#EC4899' },
    { id: '7', name: 'Maintenance', color: '#6366F1' },
    { id: '8', name: 'Other', color: '#64748B' },
  ];

  // Calculate statistics
  const stats = {
    totalExpenses: expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0),
    pendingExpenses: expenses.filter(e => e.status === 'pending').length,
    approvedExpenses: expenses.filter(e => e.status === 'approved').length,
    totalAdvances: advances.reduce((sum, adv) => sum + (parseFloat(adv.amount) || 0), 0),
    pendingAdvances: advances.filter(a => a.status === 'pending').length,
    approvedAdvances: advances.filter(a => a.status === 'approved').reduce((sum, adv) => sum + (parseFloat(adv.amount) || 0), 0),
    monthlyExpenses: expenses.filter(e => {
      const expDate = new Date(e.expenseDate);
      const monthStart = startOfMonth(new Date());
      const monthEnd = endOfMonth(new Date());
      return expDate >= monthStart && expDate <= monthEnd;
    }).reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0),
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch =
      expense.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || expense.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Filter advances
  const filteredAdvances = advances.filter(advance => {
    const matchesSearch =
      advance.staffName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      advance.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || advance.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Handle Expense Operations
  const handleAddExpense = async () => {
    try {
      if (!expenseForm.title || !expenseForm.amount || !expenseForm.category) {
        toast.error('Please fill in all required fields');
        return;
      }

      await addExpense(expenseForm);
      toast.success('Expense added successfully');
      setShowExpenseModal(false);
      resetExpenseForm();
      loadData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const handleEditExpense = async () => {
    try {
      if (!selectedItem) return;

      await updateExpense(selectedItem.id, expenseForm);
      toast.success('Expense updated successfully');
      setShowExpenseModal(false);
      setSelectedItem(null);
      resetExpenseForm();
      loadData();
    } catch (error) {
      console.error('Error updating expense:', error);
      toast.error('Failed to update expense');
    }
  };

  const handleDeleteExpense = async () => {
    try {
      if (!selectedItem) return;

      await deleteExpense(selectedItem.id);
      toast.success('Expense deleted successfully');
      setShowDeleteModal(false);
      setSelectedItem(null);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  // Handle Advance Operations
  const handleAddAdvance = async () => {
    try {
      if (!advanceForm.staffId || !advanceForm.amount || !advanceForm.reason) {
        toast.error('Please fill in all required fields');
        return;
      }

      await addAdvance(advanceForm);
      toast.success('Advance request added successfully');
      setShowAdvanceModal(false);
      resetAdvanceForm();
      loadData();
    } catch (error) {
      console.error('Error adding advance:', error);
      toast.error('Failed to add advance');
    }
  };

  const handleApproveAdvance = async (advance) => {
    try {
      await updateAdvanceStatus(advance.id, 'approved', 'Admin', 'Approved');
      toast.success('Advance approved successfully');
      loadData();
    } catch (error) {
      console.error('Error approving advance:', error);
      toast.error('Failed to approve advance');
    }
  };

  const handleRejectAdvance = async (advance) => {
    try {
      await updateAdvanceStatus(advance.id, 'rejected', 'Admin', 'Rejected');
      toast.success('Advance rejected');
      loadData();
    } catch (error) {
      console.error('Error rejecting advance:', error);
      toast.error('Failed to reject advance');
    }
  };

  const openExpenseModal = (expense = null) => {
    if (expense) {
      setModalMode('edit');
      setSelectedItem(expense);
      setExpenseForm({
        title: expense.title || '',
        category: expense.category || '',
        amount: expense.amount || '',
        expenseDate: expense.expenseDate || today,
        description: expense.description || '',
        paymentMethod: expense.paymentMethod || 'cash',
        status: expense.status || 'pending',
        receiptNumber: expense.receiptNumber || '',
        vendor: expense.vendor || '',
      });
    } else {
      setModalMode('add');
      resetExpenseForm();
    }
    setShowExpenseModal(true);
  };

  const openAdvanceModal = () => {
    resetAdvanceForm();
    setShowAdvanceModal(true);
  };

  const openDeleteModal = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      title: '',
      category: '',
      amount: '',
      expenseDate: today,
      description: '',
      paymentMethod: 'cash',
      status: 'pending',
      receiptNumber: '',
      vendor: '',
    });
  };

  const resetAdvanceForm = () => {
    setAdvanceForm({
      staffId: '',
      staffName: '',
      amount: '',
      requestDate: today,
      reason: '',
      status: 'pending',
      repaymentMethod: 'salary_deduction',
      installments: '1',
    });
  };

  // Analytics data for charts
  const expensesByCategory = categories.map(cat => ({
    name: cat.name,
    value: expenses
      .filter(e => e.category === cat.name)
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
    color: cat.color,
  })).filter(item => item.value > 0);

  const monthlyTrend = Array.from({ length: 6 }, (_, i) => {
    const date = subDays(new Date(), (5 - i) * 30);
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    
    const monthExpenses = expenses.filter(e => {
      const expDate = new Date(e.expenseDate);
      return expDate >= monthStart && expDate <= monthEnd;
    }).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
    
    return {
      month: format(date, 'MMM'),
      expenses: monthExpenses,
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading expense data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen pb-52">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900">
              Expense Management
            </h2>
            <p className="text-[11px] md:text-sm text-gray-600 mt-1">
              Track expenses, manage advances, and generate reports
            </p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'expenses' && (
              <button
                onClick={() => openExpenseModal()}
                className="flex items-center gap-2 px-4 py-2 text-xs bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-all shadow-lg font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Add Expense</span>
              </button>
            )}
            {activeTab === 'advances' && (
              <button
                onClick={openAdvanceModal}
                className="flex items-center gap-2 px-4 py-2 text-xs bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-all shadow-lg font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>New Advance</span>
              </button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Expenses"
            value={`₹${Math.round(stats.totalExpenses).toLocaleString()}`}
            icon={Receipt}
            color="red"
            subtitle={`${expenses.length} transactions`}
          />
          <StatCard
            title="This Month"
            value={`₹${Math.round(stats.monthlyExpenses).toLocaleString()}`}
            icon={Calendar}
            color="blue"
            subtitle="Current month"
          />
          <StatCard
            title="Total Advances"
            value={`₹${Math.round(stats.totalAdvances).toLocaleString()}`}
            icon={Wallet}
            color="purple"
            subtitle={`${advances.length} requests`}
          />
          <StatCard
            title="Pending Approval"
            value={stats.pendingExpenses + stats.pendingAdvances}
            icon={Clock}
            color="orange"
            subtitle="Awaiting action"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'expenses'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Expenses
          </button>
          <button
            onClick={() => setActiveTab('advances')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'advances'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Advances
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'reports'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Reports
          </button>
        </div>

        {/* Filters */}
        {(activeTab === 'expenses' || activeTab === 'advances') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters & Search</span>
              </div>
              <div className="md:hidden">
                {isFilterExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </button>

            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden  ${
                isFilterExpanded ? 'max-h-96 md:max-h-none' : 'max-h-0 md:max-h-none'
              }`}
            >
              <div className="p-4 border-t border-gray-100 space-y-4 block md:flex justify-between items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeTab === 'expenses' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                      >
                        <option value="all">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content based on active tab */}
      {activeTab === 'expenses' && (
        <ExpensesTable
          expenses={filteredExpenses}
          onEdit={openExpenseModal}
          onDelete={openDeleteModal}
        />
      )}

      {activeTab === 'advances' && (
        <AdvancesTable
          advances={filteredAdvances}
          onApprove={handleApproveAdvance}
          onReject={handleRejectAdvance}
          onDelete={openDeleteModal}
        />
      )}

      {activeTab === 'reports' && (
        <ReportsView
          expenses={expenses}
          advances={advances}
          expensesByCategory={expensesByCategory}
          monthlyTrend={monthlyTrend}
          stats={stats}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      )}

      {/* Modals */}
      {showExpenseModal && (
        <ExpenseModal
          mode={modalMode}
          form={expenseForm}
          setForm={setExpenseForm}
          categories={categories}
          onSubmit={modalMode === 'add' ? handleAddExpense : handleEditExpense}
          onClose={() => {
            setShowExpenseModal(false);
            setSelectedItem(null);
            resetExpenseForm();
          }}
        />
      )}

      {showAdvanceModal && (
        <AdvanceModal
          form={advanceForm}
          setForm={setAdvanceForm}
          staff={staff}
          onSubmit={handleAddAdvance}
          onClose={() => {
            setShowAdvanceModal(false);
            resetAdvanceForm();
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          item={selectedItem}
          onConfirm={handleDeleteExpense}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 font-medium uppercase">{title}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// Expenses Table Component
const ExpensesTable = ({ expenses, onEdit, onDelete }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Expense Details
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {expenses.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  No expenses found
                </td>
              </tr>
            ) : (
              expenses.map((expense) => (
                <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">{expense.title}</p>
                      <p className="text-sm text-gray-500">{expense.vendor || 'N/A'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-gray-900">
                      ₹{parseFloat(expense.amount || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">{expense.paymentMethod}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-900">
                      {format(new Date(expense.expenseDate), 'MMM dd, yyyy')}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        expense.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : expense.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {expense.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onEdit(expense)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(expense)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Advances Table Component  
const AdvancesTable = ({ advances, onApprove, onReject, onDelete }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Staff Member
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Reason
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Request Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {advances.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  No advance requests found
                </td>
              </tr>
            ) : (
              advances.map((advance) => (
                <tr key={advance.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <p className="font-semibold text-gray-900">{advance.staffName}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-bold text-gray-900">
                      ₹{parseFloat(advance.amount || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">{advance.repaymentMethod}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-700">{advance.reason}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-900">
                      {format(new Date(advance.requestDate), 'MMM dd, yyyy')}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        advance.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : advance.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {advance.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {advance.status === 'pending' && (
                        <>
                          <button
                            onClick={() => onApprove(advance)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onReject(advance)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => onDelete(advance)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Reports View Component
const ReportsView = ({
  expenses,
  advances,
  expensesByCategory,
  monthlyTrend,
  stats,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}) => {
  const filteredExpenses = expenses.filter(e => {
    const expDate = new Date(e.expenseDate);
    return expDate >= new Date(startDate) && expDate <= new Date(endDate);
  });

  const filteredTotal = filteredExpenses.reduce(
    (sum, e) => sum + (parseFloat(e.amount) || 0),
    0
  );

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Report Period:</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
            />
          </div>
          <div className="ml-auto">
            <p className="text-sm font-semibold text-gray-900">
              Total: ₹{Math.round(filteredTotal).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">{filteredExpenses.length} transactions</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenses by Category */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Expenses by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expensesByCategory}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">6-Month Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Bar dataKey="expenses" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-600">Approved Expenses</h4>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.approvedExpenses}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-600">Pending Approval</h4>
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.pendingExpenses}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-600">Approved Advances</h4>
            <Wallet className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            ₹{Math.round(stats.approvedAdvances).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

// Expense Modal Component
const ExpenseModal = ({ mode, form, setForm, categories, onSubmit, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            {mode === 'add' ? 'Add New Expense' : 'Edit Expense'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expense Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="e.g., Office Supplies Purchase"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expense Date
              </label>
              <input
                type="date"
                value={form.expenseDate}
                onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <input
                type="text"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Number
              </label>
              <input
                type="text"
                value={form.receiptNumber}
                onChange={(e) => setForm({ ...form, receiptNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Receipt/Invoice number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Additional details..."
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {mode === 'add' ? 'Add Expense' : 'Update Expense'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Advance Modal Component
const AdvanceModal = ({ form, setForm, staff, onSubmit, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">New Advance Request</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Staff Member <span className="text-red-500">*</span>
              </label>
              <select
                value={form.staffId}
                onChange={(e) => {
                  const selectedStaff = staff.find(s => s.id === e.target.value);
                  setForm({
                    ...form,
                    staffId: e.target.value,
                    staffName: selectedStaff?.name || '',
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="">Select staff</option>
                {staff.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Request Date
              </label>
              <input
                type="date"
                value={form.requestDate}
                onChange={(e) => setForm({ ...form, requestDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repayment Method
              </label>
              <select
                value={form.repaymentMethod}
                onChange={(e) => setForm({ ...form, repaymentMethod: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
              >
                <option value="salary_deduction">Salary Deduction</option>
                <option value="lump_sum">Lump Sum</option>
                <option value="installments">Installments</option>
              </select>
            </div>

            {form.repaymentMethod === 'installments' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Installments
                </label>
                <input
                  type="number"
                  value={form.installments}
                  onChange={(e) => setForm({ ...form, installments: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                  min="1"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="Reason for advance request..."
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Modal Component
const DeleteModal = ({ item, onConfirm, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Delete</h3>
          <p className="text-gray-700 mb-6">
            Are you sure you want to delete this item? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseManagement;
